import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/settings/settings-common.css';
import '../../styles/settings/Settings.css';

import Profile from './profile/Profile';
import Contacts from './contacts/Contacts';
import UserSettings from './prefs/UserSettings';
import UserManagement from './user-management/UserManagement';
import SystemSettings from './system-settings/SystemSettings';
import Customization from './customization/Customization';

const SCROLL_OFFSET_VIEWPORT_RATIO = 0.2;
const SCROLL_OFFSET_THEME_REDUCE = 48;
const SCROLL_SPY_DEBOUNCE_MS = 100;
const PROGRAMMATIC_SCROLL_GUARD_MS = 800;

const getScrollOffsetPx = (scrollable) => {
    if (!scrollable) return 80;
    return Math.round(scrollable.clientHeight * SCROLL_OFFSET_VIEWPORT_RATIO);
};

const HASH_TO_TARGET = {
    profile: 'settings-section-profile',
    contacts: 'settings-section-contacts',
    'prefs-security': 'user-settings-section-security',
    'prefs-profile': 'user-settings-section-profile',
    'prefs-notifications': 'user-settings-section-notifications',
    'prefs-mfa': 'user-settings-section-mfa',
    'user-management': 'settings-section-user-management',
    'system-settings-general': 'system-settings-section-general',
    'system-settings-oauth': 'system-settings-section-oauth',
    'system-settings-file-upload': 'system-settings-section-file-upload',
    'customization-branding': 'customization-section-branding',
    'customization-contact': 'customization-section-contact',
    'customization-theme': 'customization-section-theme',
};

const TARGET_TO_HASH = Object.fromEntries(
    Object.entries(HASH_TO_TARGET).map(([hash, targetId]) => [targetId, hash])
);

const LEGACY_PATH_TO_HASH = {
    profile: 'profile',
    contacts: 'contacts',
    prefs: 'prefs-security',
    'user-management': 'user-management',
    'system-settings': 'system-settings-general',
    customization: 'customization-branding',
};

const Settings = () => {
    const { t } = useTranslation();
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const mainContentRef = useRef(null);
    const isProgrammaticScrollRef = useRef(false);
    const scrollSpyHashRef = useRef(null);
    const scrollAfterThemeReloadRef = useRef(false);
    const hashRaw = location.hash ? location.hash.slice(1) : '';
    const hashRawRef = useRef(hashRaw);
    hashRawRef.current = hashRaw;
    const pathSuffix = location.pathname.replace(/^\/settings\/?/, '') || null;

    useEffect(() => {
        const savedHash = localStorage.getItem('settingsHashAfterReload');
        if (savedHash) {
            localStorage.removeItem('settingsHashAfterReload');
            if (savedHash in HASH_TO_TARGET) {
                scrollAfterThemeReloadRef.current = true;
                navigate(`/settings#${savedHash}`, { replace: true });
                return;
            }
        }
        if (pathSuffix && LEGACY_PATH_TO_HASH[pathSuffix]) {
            navigate(`/settings#${LEGACY_PATH_TO_HASH[pathSuffix]}`, { replace: true });
            return;
        }
        if (!pathSuffix && !hashRaw) {
            navigate('/settings#profile', { replace: true });
        }
    }, [pathSuffix, hashRaw, navigate]);

    useEffect(() => {
        if (!isAdmin && (hashRaw === 'user-management' || hashRaw.startsWith('system-settings-') || hashRaw.startsWith('customization-'))) {
            navigate('/settings#profile', { replace: true });
        }
    }, [hashRaw, isAdmin, navigate]);

    const isActive = (sect, sub) => {
        if (sub) {
            if (sect === 'prefs') return hashRaw === `prefs-${sub}`;
            if (sect === 'system-settings') return hashRaw === `system-settings-${sub}`;
            if (sect === 'customization') return hashRaw === `customization-${sub}`;
            return hashRaw === `${sect}-${sub}`;
        }
        return hashRaw === sect;
    };

    const isSubActive = (sect, sub) => {
        if (sect === 'prefs') return hashRaw === `prefs-${sub}`;
        if (sect === 'system-settings') return hashRaw === `system-settings-${sub}`;
        if (sect === 'customization') return hashRaw === `customization-${sub}`;
        return hashRaw === `${sect}-${sub}`;
    };

    const userNavItems = [
        { id: 'profile', path: 'profile', label: t('layout.navigation.profile'), subOptions: null },
        { id: 'contacts', path: 'contacts', label: t('layout.navigation.contacts'), subOptions: null },
        {
            id: 'prefs',
            path: 'prefs',
            label: t('settings.prefs'),
            subOptions: [
                { id: 'security', label: t('userSettings.tabs.security.label') },
                { id: 'profile', label: t('userSettings.tabs.profile.label') },
                { id: 'notifications', label: t('userSettings.tabs.notifications.label') },
                { id: 'mfa', label: t('userSettings.tabs.mfa.label') },
            ],
        },
    ];

    const adminNavItems = [
        { id: 'user-management', path: 'user-management', label: t('layout.navigation.userManagement'), subOptions: null },
        {
            id: 'system-settings',
            path: 'system-settings',
            label: t('layout.navigation.systemSettings'),
            subOptions: [
                { id: 'general', label: t('admin.systemSettings.generalSettings') },
                { id: 'oauth', label: t('admin.systemSettings.oauthSettings') },
                { id: 'file-upload', label: t('admin.systemSettings.fileUploadSettings') },
            ],
        },
        {
            id: 'customization',
            path: 'customization',
            label: t('layout.navigation.customization'),
            subOptions: [
                { id: 'branding', label: t('admin.customization.branding') },
                { id: 'contact', label: t('admin.customization.contact') },
                { id: 'theme', label: t('admin.customization.theme') },
            ],
        },
    ];

    const scrollToSection = useCallback((targetId) => {
        const target = document.getElementById(targetId);
        const scrollable = mainContentRef.current;
        if (!target || !scrollable) return;
        isProgrammaticScrollRef.current = true;
        const offset = getScrollOffsetPx(scrollable) - (targetId === 'customization-section-theme' ? SCROLL_OFFSET_THEME_REDUCE : 0);
        const top = target.getBoundingClientRect().top - scrollable.getBoundingClientRect().top + scrollable.scrollTop - offset;
        scrollable.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        setTimeout(() => { isProgrammaticScrollRef.current = false; }, PROGRAMMATIC_SCROLL_GUARD_MS);
    }, []);

    const getCurrentSectionHash = useCallback(() => {
        const scrollable = mainContentRef.current;
        if (!scrollable) return null;
        const activationLine = scrollable.scrollTop + getScrollOffsetPx(scrollable);
        const targetIds = Object.values(HASH_TO_TARGET);
        let best = null;
        let bestTop = -Infinity;
        for (const id of targetIds) {
            const el = document.getElementById(id);
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            const scrollableRect = scrollable.getBoundingClientRect();
            const elTop = rect.top - scrollableRect.top + scrollable.scrollTop;
            const elBottom = elTop + el.offsetHeight;
            if (elTop <= activationLine && activationLine < elBottom) {
                return TARGET_TO_HASH[id];
            }
            if (elTop <= activationLine && elTop > bestTop) {
                best = TARGET_TO_HASH[id];
                bestTop = elTop;
            }
        }
        return best;
    }, []);

    useEffect(() => {
        if (!hashRaw || !HASH_TO_TARGET[hashRaw]) return;
        if (scrollSpyHashRef.current === hashRaw && !scrollAfterThemeReloadRef.current) {
            scrollSpyHashRef.current = null;
            return;
        }
        const targetId = HASH_TO_TARGET[hashRaw];
        const delay = scrollAfterThemeReloadRef.current ? 500 : 0;
        if (scrollAfterThemeReloadRef.current) scrollAfterThemeReloadRef.current = false;
        const timer = setTimeout(() => {
            const attemptScroll = (retries = 0) => {
                const target = document.getElementById(targetId);
                if (target) {
                    scrollToSection(targetId);
                } else if (retries < 5) {
                    setTimeout(() => attemptScroll(retries + 1), 150);
                }
            };
            requestAnimationFrame(() => requestAnimationFrame(() => attemptScroll()));
        }, delay);
        return () => clearTimeout(timer);
    }, [hashRaw, scrollToSection]);

    useEffect(() => {
        const scrollable = mainContentRef.current;
        if (!scrollable) return;
        let debounceTimer = null;
        const onScroll = () => {
            if (isProgrammaticScrollRef.current) return;
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                debounceTimer = null;
                const hash = getCurrentSectionHash();
                if (hash && hash !== hashRawRef.current) {
                    scrollSpyHashRef.current = hash;
                    navigate(`/settings#${hash}`, { replace: true });
                }
            }, SCROLL_SPY_DEBOUNCE_MS);
        };
        scrollable.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            scrollable.removeEventListener('scroll', onScroll);
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [navigate, getCurrentSectionHash]);

    const handleNavClick = (item, subId) => {
        if (subId) {
            const hash = `${item.path}-${subId}`;
            navigate(`/settings#${hash}`, { replace: false });
        } else {
            if (item.subOptions) {
                const hash = `${item.path}-${item.subOptions[0].id}`;
                navigate(`/settings#${hash}`, { replace: false });
            } else {
                navigate(`/settings#${item.path}`, { replace: false });
            }
        }
    };

    const renderNavItem = (item) => {
        const hasSubs = item.subOptions && item.subOptions.length > 0;

        return (
            <div key={item.id} className="settings-nav-item-wrapper">
                <button
                    className={`settings-nav-item ${!hasSubs && isActive(item.path) ? 'active' : ''}`}
                    onClick={() => handleNavClick(item)}
                >
                    <span className="settings-nav-label">{item.label}</span>
                </button>
                {hasSubs && (
                    <div className="settings-nav-sub">
                        {item.subOptions.map(sub => (
                            <button
                                key={sub.id}
                                className={`settings-nav-sub-item ${isSubActive(item.path, sub.id) ? 'active' : ''}`}
                                onClick={() => handleNavClick(item, sub.id)}
                            >
                                {sub.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`settings-container${isAdmin ? ' settings-container--admin' : ''}`}>
            <div className="settings-sidebar">
                <div className="settings-nav-section">
                    {userNavItems.map(renderNavItem)}
                </div>
                {isAdmin && (
                    <div className="settings-nav-section">
                        {adminNavItems.map(renderNavItem)}
                    </div>
                )}
            </div>
            <div className="settings-main settings-main-scroll" ref={mainContentRef} data-settings-scroll>
                <div className="settings-main-inner settings-main-inner-unified">
                    <section id="settings-section-profile" className="settings-section">
                        <Profile embedded unifiedPage />
                    </section>
                    <section id="settings-section-contacts" className="settings-section settings-section-unified">
                        <Contacts embedded unifiedPage />
                    </section>
                    <section id="settings-section-prefs" className="settings-section">
                        <UserSettings embedded unifiedPage />
                    </section>
                    {isAdmin && (
                        <>
                            <section id="settings-section-user-management" className="settings-section settings-section-unified">
                                <UserManagement embedded unifiedPage />
                            </section>
                            <section id="settings-section-system-settings" className="settings-section settings-section-unified">
                                <SystemSettings embedded unifiedPage />
                            </section>
                            <section id="settings-section-customization" className="settings-section settings-section-unified">
                                <Customization embedded unifiedPage />
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;
